import moment from 'moment/moment';
import { oneDecimal } from '../../shipment/useUpdatingDuration';
import type { SuggestionWithMetrics } from './SuggestionEstimator';
import { sumBy } from 'lodash';
import type { TimelineItem } from '../react-calendar-timeline';
import type { TimelineSuggestion } from '../types';
import type { TimeData } from '../../core/TimeData';
import { isDefined } from '../../../lib/util';
import type { ScenarioLocation } from '../../../lib/planningData';
import {
  calculateTceRange,
  getTargetShipmentForSuggestion,
} from '../../core/Core.voyage-metrics';

export function getVoyageStartDates(
  suggestions: SuggestionWithMetrics[],
): string[] {
  return suggestions.map((suggestion) => {
    const start = suggestion.suggestionVoyageStart;
    if (!start) {
      return 'N/A';
    }
    return moment(start).format('MMM D, YYYY');
  });
}

export function getVoyageEndDates(
  suggestions: SuggestionWithMetrics[],
): string[] {
  return suggestions.map((suggestion) => {
    const end = suggestion.suggestionVoyageEnd;
    if (!end) {
      return 'N/A';
    }
    return moment(end).format('MMM D, YYYY');
  });
}

export function formatTotalPortSeaDays(
  suggestions: SuggestionWithMetrics[],
): string[] {
  return suggestions.map((suggestion) => {
    const totalDays = oneDecimal(suggestion.durationInDays);
    const seaDays = oneDecimal(suggestion.seaDurationInDays);
    const portDays = oneDecimal(suggestion.portActivitiesInDays);

    if (suggestion.durationInDays < 0.01) {
      return 'N/A / N/A / N/A';
    }

    return `${totalDays} / ${portDays} / ${seaDays}`;
  });
}

export function getTotalTcCost(
  timelineSuggestion: TimelineSuggestion,
): number | null {
  const tcHourlyCost = timelineSuggestion.suggestion?.schedule.inputs?.tcRate;
  if (!tcHourlyCost || tcHourlyCost <= 0) {
    return null;
  }

  const totalHours = timelineSuggestion.suggestion.durationInDays * 24;

  const totalTcCost = totalHours * tcHourlyCost;
  return totalTcCost > 0 ? totalTcCost : null;
}

export function hasTcRateData(suggestions: SuggestionWithMetrics[]): boolean {
  return suggestions.some((suggestion) => {
    const tcRate = suggestion.schedule?.inputs?.tcRate;
    return tcRate != null && tcRate > 0;
  });
}

export function getTotalDistance(timeData: TimeData[]): number {
  return sumBy(timeData, (td) => td.distance ?? 0);
}

export function getCargoSummaries(
  suggestions: SuggestionWithMetrics[],
): string[] {
  return suggestions.map((suggestion) => {
    const { schedule, entries } = suggestion;
    if (!schedule || !schedule.timeData || !entries || entries.length === 0) {
      return 'N/A';
    }

    const orderIds = new Set(entries.map((entry) => entry.orderId));

    const relevantAssignments =
      schedule.combination?.assignments?.filter((a) =>
        orderIds.has(a.order.id),
      ) || [];

    const assortmentMap = new Map<string, { amount: number; unit: string }>();

    for (const assignment of relevantAssignments) {
      const order = assignment.order;
      if (!order) continue;

      const assortmentName = order.assortmentByAssortmentId?.name ?? 'Unknown';
      const amount = order.cargoAmount ? +order.cargoAmount : 0;
      const amountUnit = order.cargoUnitByCargoUnitId?.name || '';

      const key = `${assortmentName}|${amountUnit}`;
      const existing = assortmentMap.get(key);

      if (existing) {
        existing.amount += amount;
      } else {
        assortmentMap.set(key, { amount, unit: amountUnit });
      }
    }

    if (assortmentMap.size === 0) {
      return 'No cargo';
    }

    const cargoItems: string[] = [];

    for (const [key, data] of assortmentMap.entries()) {
      const assortmentName = key.split('|')[0];
      const unitPart = data.unit ? `${data.unit} ` : '';
      cargoItems.push(
        `${Math.round(data.amount)} ${unitPart}${assortmentName}`,
      );
    }

    return cargoItems.join(', ');
  });
}

export const getSuggestionTitle = (
  suggestion: SuggestionWithMetrics,
  index: number,
) => {
  const vesselName = suggestion.vesselName
    ? `${suggestion.vesselName} ${index + 1}`
    : `Vessel ${index + 1}`;

  if (suggestion.fromPort && suggestion.toPort) {
    return `${vesselName}: ${suggestion.fromPort} → ${suggestion.toPort}`;
  }

  return vesselName;
};

export function extractPortNames(
  timeData: TimeData[],
  locations?: Map<number, ScenarioLocation>,
) {
  return timeData
    .filter((td) => locations && td.event && 'location' in td.event)
    .map((td) => ('location' in td.event ? td.event.location?.name : null))
    .filter(isDefined);
}

export function getPortInfo(
  portNames: (string | undefined | null)[],
  fromIndex: number,
  toIndex: number,
) {
  const fromPort = portNames[fromIndex] || '';
  const toPort = portNames[toIndex] || '';
  return { fromPort, toPort };
}

export function prepareSuggestionMetrics(
  selectedSuggestions: TimelineItem<TimelineSuggestion>[],
) {
  return selectedSuggestions
    .map((timelineSuggestion) => {
      if (!timelineSuggestion.suggestion) {
        return null;
      }

      const { locations } = timelineSuggestion.scheduleContext || {};
      const { timeData } = calculateTceRange(
        timelineSuggestion.suggestion.schedule,
        getTargetShipmentForSuggestion(
          timelineSuggestion.suggestion.schedule.combination,
        ),
        'overlapping_voyage',
      );
      const portNames = extractPortNames(timeData, locations);
      const { loadIndex, dischargeIndex } =
        timelineSuggestion.suggestion.schedule.combination.assignments[0];
      const { fromPort, toPort } = getPortInfo(
        portNames,
        loadIndex,
        dischargeIndex,
      );

      const vesselName = timelineSuggestion.groupObj.timeline.vessel?.name;
      const vesselId = timelineSuggestion.groupObj.timeline.vesselId;

      const totalTcCost = getTotalTcCost(timelineSuggestion);

      return {
        ...timelineSuggestion.suggestion,
        id: timelineSuggestion.id,
        fromPort,
        toPort,
        vesselName,
        vesselId,
        totalTcCost,
        suggestionIndex: timelineSuggestion.suggestionIndex,
      };
    })
    .filter(isDefined);
}
